import * as React from "react";
import { Select } from "@base-ui/react/select";
import { useSeedsPortalContainer } from "./SeedsPortal";
import SelectItem from "./Common/SelectItem";
import type { DataWithId } from "./Common/types";
import { groupItems } from "./Common/groupItems";
import {
  Field,
  SelectTrigger,
  SelectIcon,
  SelectGroup,
  SelectGroupLabel,
  SelectSeparator,
  SelectPositioner,
  SelectPopup,
} from "./Common/SelectStyles";
import { StyledChevron, ChevronIcon } from "./Common/SelectIcons";
import { cn, type WithStringClassName } from "./Common/cn";

// <Listbox(?!\.|B)
// ─── CSS-class components (see menu.css) ────────────────────────────────────

const StyledValue = React.forwardRef<
  React.ElementRef<typeof Select.Value>,
  WithStringClassName<React.ComponentProps<typeof Select.Value>>
>(({ className, ...rest }, ref) => (
  <Select.Value
    ref={ref}
    className={cn("seeds-select-value-text", className)}
    {...rest}
  />
));
StyledValue.displayName = "StyledValue";

// ─── Props ────────────────────────────────────────────────────────────────────

interface SingleSelectBaseProps {
  id?: string;
  placeholder?: string;
  includePlaceholderItem?: boolean;
  disabled?: boolean;
  "aria-label"?: string;
  /** Id of a visible label. Preferred over `aria-label`. */
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  isInvalid?: boolean;
  required?: boolean;
  fullWidth?: boolean;
}

interface SingleSelectDataProps<T extends DataWithId>
  extends SingleSelectBaseProps {
  data: T[];
  itemToString: (item: T | null) => string;
  renderItem?: (item: T) => React.ReactNode;
  inputType?: "icon" | "radio";
  selectedItemId?: T["id"] | null;
  defaultSelectedItemId?: T["id"] | null;
  onSelectedItemIdChange?: (id: T["id"] | null) => void;
  renderSelection?: (item: T) => React.ReactNode;
  groupBy?: (item: T) => string;
  renderGroupHeading?: (label: string) => React.ReactNode;
  children?: never;
}

interface SingleSelectChildrenProps extends SingleSelectBaseProps {
  children: React.ReactNode;
  selectedItemId?: string | number | null;
  defaultSelectedItemId?: string | number | null;
  onSelectedItemIdChange?: (id: string | number | null) => void;
  data?: never;
  itemToString?: never;
  renderItem?: never;
  inputType?: never;
  renderSelection?: never;
  groupBy?: never;
  renderGroupHeading?: never;
}

export type SingleSelectProps<T extends DataWithId> =
  | SingleSelectDataProps<T>
  | SingleSelectChildrenProps;

// ─── Component ────────────────────────────────────────────────────────────────

export function SingleSelect<T extends DataWithId>(
  props: SingleSelectProps<T>
) {
  const { id, placeholder, includePlaceholderItem, fullWidth = true } = props;
  const ariaDescribedBy = props["aria-describedby"];
  const ariaLabel = props["aria-label"];
  const ariaLabelledBy = props["aria-labelledby"];
  const isInvalid = props.isInvalid;

  const isChildrenMode = "children" in props && props.children != null;

  const selectedItemId = props.selectedItemId;
  const defaultSelectedItemId = props.defaultSelectedItemId;
  const onSelectedItemIdChange = props.onSelectedItemIdChange;

  const data = isChildrenMode ? [] : (props as SingleSelectDataProps<T>).data;
  const itemToString = isChildrenMode
    ? () => ""
    : (props as SingleSelectDataProps<T>).itemToString;
  const renderItem = isChildrenMode
    ? undefined
    : (props as SingleSelectDataProps<T>).renderItem;
  const inputType = isChildrenMode
    ? "icon"
    : (props as SingleSelectDataProps<T>).inputType ?? "icon";
  const renderSelection = isChildrenMode
    ? undefined
    : (props as SingleSelectDataProps<T>).renderSelection;
  const groupBy = isChildrenMode
    ? undefined
    : (props as SingleSelectDataProps<T>).groupBy;
  const renderGroupHeading = isChildrenMode
    ? undefined
    : (props as SingleSelectDataProps<T>).renderGroupHeading;
  const children = isChildrenMode
    ? (props as SingleSelectChildrenProps).children
    : undefined;

  const { containerRef, portalContainer } = useSeedsPortalContainer();
  const items = React.useMemo(() => {
    const dataItems = data.map((item) => ({
      value: String(item.id),
      label: itemToString(item),
    }));
    if (includePlaceholderItem) {
      return [{ value: null, label: placeholder ?? "" }, ...dataItems];
    }
    return dataItems;
  }, [data, itemToString, includePlaceholderItem, placeholder]);

  const dataWithPlaceholder = includePlaceholderItem
    ? [{ id: null, value: null, label: placeholder ?? "" }, ...data]
    : data;

  const isControlled = selectedItemId !== undefined;
  const [internalValue, setInternalValue] = React.useState<string | null>(
    defaultSelectedItemId != null ? String(defaultSelectedItemId) : null
  );
  const value = isControlled
    ? selectedItemId != null
      ? String(selectedItemId)
      : null
    : internalValue;

  const selectedItem = React.useMemo(
    () =>
      value != null ? data.find((d) => String(d.id) === value) ?? null : null,
    [data, value]
  );

  function handleValueChange(newValue: string | null) {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    if (onSelectedItemIdChange) {
      if (newValue == null) {
        onSelectedItemIdChange(null);
      } else {
        const item = data.find((d) => String(d.id) === newValue);
        onSelectedItemIdChange(item ? item.id : null);
      }
    }
  }

  return (
    <Field $fullWidth={fullWidth}>
      <div ref={containerRef} style={{ position: "relative" }} />
      <Select.Root
        items={items}
        value={value}
        disabled={props.disabled}
        onValueChange={handleValueChange}
        id={id}
      >
        <SelectTrigger
          aria-describedby={ariaDescribedBy}
          aria-invalid={isInvalid || undefined}
          aria-required={props.required || undefined}
          $isInvalid={isInvalid}
        >
          <StyledValue placeholder={placeholder}>
            {renderSelection && selectedItem
              ? renderSelection(selectedItem)
              : undefined}
          </StyledValue>
          <SelectIcon>
            <StyledChevron data-chevron>
              <ChevronIcon />
            </StyledChevron>
          </SelectIcon>
        </SelectTrigger>
        <Select.Portal container={portalContainer}>
          <SelectPositioner sideOffset={8} alignItemWithTrigger={false}>
            <SelectPopup>
              <Select.ScrollUpArrow />
              <Select.List
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
              >
                {children ? (
                  children
                ) : groupBy ? (
                  groupItems(data, groupBy).map((group, index, arr) => (
                    <React.Fragment key={group.value}>
                      <SelectGroup>
                        <SelectGroupLabel>
                          {renderGroupHeading
                            ? renderGroupHeading(group.value)
                            : group.value}
                        </SelectGroupLabel>
                        {group.items.map((item) => (
                          <SelectItem
                            key={item.id}
                            value={String(item.id)}
                            label={itemToString(item)}
                            disabled={item.disabled}
                            inputType={inputType}
                            renderItem={
                              renderItem
                                ? () => renderItem(item)
                                : () => itemToString(item)
                            }
                          />
                        ))}
                      </SelectGroup>
                      {index < arr.length - 1 && <SelectSeparator />}
                    </React.Fragment>
                  ))
                ) : (
                  <>
                    {includePlaceholderItem && (
                      <SelectItem
                        key={"placeholder"}
                        value={null}
                        label={placeholder ?? ""}
                      />
                    )}
                    {data.map((item) => (
                      <SelectItem
                        key={item.id}
                        value={String(item.id)}
                        label={itemToString(item)}
                        disabled={item.disabled}
                        inputType={inputType}
                        renderItem={
                          renderItem
                            ? () => renderItem(item)
                            : () => itemToString(item)
                        }
                      />
                    ))}
                  </>
                )}
              </Select.List>
              <Select.ScrollDownArrow />
            </SelectPopup>
          </SelectPositioner>
        </Select.Portal>
      </Select.Root>
    </Field>
  );
}
